import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {OrderService} from '../../services/order.service';
import {BehaviorSubject, filter, firstValueFrom, take, timeout} from 'rxjs';
import {MatSnackBar} from '@angular/material/snack-bar';
import {ListingService} from '../../services/listing.service';
import {TicketsService} from '../../services/tickets.service';
import {UserService} from '../../services/user.service';
import environment from '../../../environment';
import {getCheckoutSelection} from './checkout-route';
import {isValidTemporaryLink} from '../listing/temporary-link';

@Component({
  selector: 'checkout',
  templateUrl: './checkout.component.html',
  styleUrls: ['./checkout.component.scss'],
  standalone: false,
})
export class CheckoutComponent implements OnDestroy, OnInit {
  public user: any;
  public loading: boolean;
  public loadError: string | null = null;
  public loadErrorTitle = 'Unable to load checkout';
  public checkoutItemTitle: string | null = null;
  private timer = null;
  private readonly iframeTTL = 1000 * 60 * 5;
  public variant: any;
  public product: any;
  private temporaryLinkHash: string | null = null;

  public summary: any = {
    options: {},
    billingAddress: {},
    shippingAddress: {},
    total: 0,
  };

  public status = {
    optionsSet: false,
    billingAddressSet: false,
    shippingAddressSet: false,
  };

  public isDeliverable: boolean = false;

  public activePanel = new BehaviorSubject<string>('options');

  public isFree: any;
  private readonly userLoadTimeout = 10000;

  constructor(
    @Inject('WorldPayCheckoutLibrary')
    private readonly worldPayCheckoutLibrary: any,
    private readonly orderService: OrderService,
    private readonly route: ActivatedRoute,
    private readonly listingService: ListingService,
    private readonly ticketsService: TicketsService,
    private readonly userService: UserService,
    private readonly snackBar: MatSnackBar
  ) {
    this.setOptions = this.setOptions.bind(this);
    this.setBillingAddress = this.setBillingAddress.bind(this);
    this.setShippingAddress = this.setShippingAddress.bind(this);
  }

  async ngOnInit(): Promise<void> {
    this.loading = true;
    const selection = getCheckoutSelection(this.route.snapshot.queryParamMap);

    if (!selection) {
      this.redirectTo('/');
      return;
    }

    try {
      const [product, user] = await Promise.all([
        this.listingService.load(selection.productId),
        this.getCurrentUser(),
      ]);

      const tempHash = this.route.snapshot.queryParamMap.get('temp');
      const hasTemporaryAccess = isValidTemporaryLink(
        product?.id,
        product?.slug,
        tempHash
      );

      if (
        !this.isPurchasableProduct(product, selection.productId, hasTemporaryAccess)
      ) {
        this.redirectTo('/');
        return;
      }

      this.product = product;
      this.temporaryLinkHash = hasTemporaryAccess ? tempHash : null;

      if (!user) {
        this.setLoadError('We could not load your account. Please try again.');
        return;
      }

      const variant = await this.loadVariant(product, selection.variantId);

      if (!variant) {
        this.setLoadError(
          'This checkout option is no longer available. Please choose another.'
        );
        return;
      }

      this.checkoutItemTitle = variant.title || product.title;

      if (product.features.product) {
        try {
          const eligibility = await this.orderService.createOrder({
            variantId: variant.id,
            checkOnly: true,
          });

          if (eligibility?.eligible !== true) {
            throw new Error('Unexpected eligibility response');
          }
        } catch (error) {
          const alreadyPurchased =
            error?.error?.error === 'You already have a pre-existing order';
          this.setLoadError(
            alreadyPurchased
              ? 'You have already purchased this product.'
              : 'Checkout is unavailable right now. Please try again in a moment.',
            alreadyPurchased
              ? 'Cannot complete purchase'
              : 'Unable to load checkout'
          );
          return;
        }
      }

      this.user = user;
      this.variant = variant;
      this.isFree =
        0 ===
        parseInt(
          Object.values(this.variant.prices)
            .map((price: any) => price.price)
            .reduce((total, price) => total + price, 0)
        );
      this.loading = false;
    } catch (error) {
      if (environment.settings.verboseLogging) {
        console.error('Error loading checkout:', error);
      }
      this.setLoadError('We could not load this checkout. Please try again.');
    }
  }

  setPanel(panelName) {
    this.activePanel.next(panelName);
  }

  setOptions(options) {
    const {delivery = {}} = options || {};
    const {amount: hasDelivery = null} = delivery;

    this.isDeliverable = !!hasDelivery;

    const value = this.reduceOptions(options);
    this.setSummary('options', value);
    this.calculateTotal(options);
    const freeNonDeliverable = this.isFree && !this.isDeliverable;
    const freeDeliverable = this.isFree && this.isDeliverable;

    switch (true) {
      case freeNonDeliverable:
        this.setBillingAddress();
        break;
      case freeDeliverable:
        this.setBillingAddress();
        this.activePanel.next('shipping');
        break;
      default:
        this.activePanel.next('billing');
        break;
    }
  }

  setSummary(key, value) {
    this.summary[key] = value;
    this.status[`${key}Set`] = true;
  }

  reduceOptions(options) {
    return Object.keys(options)
      .filter(slug => options[slug].amount)
      .reduce((tickets, slug) => {
        tickets[slug] = options[slug];

        return tickets;
      }, {});
  }

  calculateTotal(options) {
    this.summary.total = 0;
    Object.values(this.variant.prices).forEach((price: any) => {
      if (options.hasOwnProperty(price.slug)) {
        const numberOfTickets = options[price.slug].amount;
        this.summary.total += price.price * numberOfTickets;
      }
    });
  }

  setBillingAddress(billingAddress = {}) {
    const {first_name: firstName, last_name: surname, email} = this.user;

    this.setSummary('billingAddress', {
      firstName,
      surname,
      email,
      ...billingAddress,
    });

    switch (true) {
      case this.isDeliverable:
        this.activePanel.next('shipping');
        break;
      case this.isFree:
        this.doSomethingForFreebies();
        break;
      default:
        this.getIframeUrl();
        break;
    }
  }

  setShippingAddress(shippingAddress = {}) {
    const {first_name: firstName, last_name: surname, email} = this.user;

    this.setSummary('shippingAddress', {
      firstName,
      surname,
      email,
      ...shippingAddress,
    });

    !this.isFree ? this.getIframeUrl() : this.doSomethingForFreebies();
  }

  doSomethingForFreebies() {
    this.activePanel.next('free-purchase');
  }

  async registerOrder() {
    Object.keys(this.status).forEach(field => (this.status[field] = false));

    const options = Object.keys(this.summary.options).reduce(
      (options, slug) => {
        options[slug] = this.summary.options[slug].amount;

        return options;
      },
      {}
    );
    const order = {
      variantId: this.variant.id,
      options: options,
      billingAddress: this.summary.billingAddress,
      shippingAddress: this.summary.shippingAddress,
      priceVersion: this.variant.features.priceVersion,
    };

    this.activePanel.next(null);

    return await this.orderService.createOrder(order);
  }

  async freePurchase() {
    try {
      this.loading = true;
      const order = await this.registerOrder();
      this.loading = false;
      this.redirectTo(`/order/${this.assertOrderId(order)}/success`);
    } catch (error) {
      this.badResponse(error);
    }
  }

  async getIframeUrl() {
    try {
      const result = await this.registerOrder();
      this.activePanel.next('card');
      const publicOrigin = this.getPublicOrigin();
      const orderId = this.assertOrderId(result.orderId);

      const customOptions = {
        iframeIntegrationId: 'libraryObject',
        iframeHelperURL: new URL('/worldpay-helper.html', publicOrigin).href,
        iframeBaseURL: publicOrigin,
        type: 'iframe',
        target: 'worldpay-card-details',
        language: 'en',
        country: 'gb',
      };

      const options = {
        ...customOptions,
        url: this.assertWorldPayUrl(result.redirectUrl),
        successURL: this.getOrderStatusUrl(publicOrigin, orderId, 'success'),
        failureURL: this.getOrderStatusUrl(publicOrigin, orderId, 'failure'),
        cancelURL: this.getOrderStatusUrl(publicOrigin, orderId, 'cancel'),
        errorURL: this.getOrderStatusUrl(publicOrigin, orderId, 'error'),
      };

      const WorldPayLibraryObject = new this.worldPayCheckoutLibrary.Library();
      WorldPayLibraryObject.setup(options);
      dispatchEvent(new Event('load'));

      this.setIframeTimer();
    } catch (error) {
      this.badResponse(error);
    }
  }

  trashIframe() {
    this.close();
  }

  setIframeTimer() {
    if (this.timer) {
      clearTimeout(this.timer);
    }
    this.timer = setTimeout(() => this.trashIframe(), this.iframeTTL);
  }

  badResponse(response) {
    const message = response?.error?.error ?? 'Something went wrong';
    this.snackBar.open(message, 'Close', {duration: 5000});
    this.loadError = message;
    this.loading = false;
    this.activePanel.next(null);
  }

  close() {
    this.redirectTo('/');
  }

  get productUrl(): string | null {
    if (!this.product?.slug) {
      return null;
    }

    const path = `/listing/${this.product.id}/${encodeURIComponent(this.product.slug)}`;
    return this.temporaryLinkHash
      ? `${path}?${new URLSearchParams({temp: this.temporaryLinkHash}).toString()}`
      : path;
  }

  ngOnDestroy(): void {
    if (this.timer) {
      clearTimeout(this.timer);
    }
  }

  private getPublicOrigin(): string {
    const publicOrigin = new URL(environment.settings.publicAppOrigin);
    const isLocal = ['localhost', '127.0.0.1'].includes(publicOrigin.hostname);

    if (publicOrigin.protocol !== 'https:' && !isLocal) {
      throw new Error('Invalid public application origin');
    }

    return publicOrigin.origin;
  }

  private assertWorldPayUrl(rawUrl: string): string {
    const paymentUrl = new URL(rawUrl);
    const allowedOrigins = environment.settings.worldPayPaymentOrigins || [];

    if (
      paymentUrl.protocol !== 'https:' ||
      !allowedOrigins.includes(paymentUrl.origin)
    ) {
      throw new Error('Unexpected payment provider URL');
    }

    return paymentUrl.href;
  }

  private assertOrderId(orderId: string): string {
    if (
      typeof orderId !== 'string' ||
      !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(orderId)
    ) {
      throw new Error('Unexpected order identifier');
    }

    return orderId;
  }

  private getOrderStatusUrl(
    publicOrigin: string,
    orderId: string,
    status: 'success' | 'failure' | 'cancel' | 'error'
  ): string {
    return new URL(`/order/${orderId}/${status}`, publicOrigin).href;
  }

  private isPurchasableProduct(
    product: any,
    productId: number,
    hasTemporaryAccess: boolean
  ): boolean {
    const features = product?.features;

    return (
      product !== null &&
      typeof product === 'object' &&
      product.success !== false &&
      Number(product.id) === productId &&
      (product.branch_id || product.enabled || hasTemporaryAccess) &&
      features !== null &&
      typeof features === 'object' &&
      !!features.purchasable &&
      !!(features.product || features.ticket || features.ballot)
    );
  }

  private async loadVariant(product: any, variantId: number): Promise<any> {
    const loaders: Promise<any[]>[] = [];

    if (product.features.product) {
      loaders.push(
        this.listingService
          .loadListingProducts(product.id)
          .then(variants => (Array.isArray(variants) ? variants : []))
      );
    }

    if (product.features.ticket || product.features.ballot) {
      loaders.push(
        this.ticketsService
          .load(product.id)
          .then(response => this.extractTickets(response))
      );
    }

    const results = await Promise.allSettled(loaders);
    const fulfilledResults = results.filter(
      (result): result is PromiseFulfilledResult<any[]> =>
        result.status === 'fulfilled'
    );

    if (fulfilledResults.length === 0) {
      throw (results[0] as PromiseRejectedResult)?.reason;
    }

    const variants = fulfilledResults.flatMap(result => result.value);

    const variant = variants.find(item => Number(item?.id) === variantId);

    return this.isVariantValid(variant, product.features) ? variant : null;
  }

  private extractTickets(response: any): any[] {
    if (
      !response?.dates ||
      response.dates === null ||
      typeof response.dates !== 'object'
    ) {
      return [];
    }

    return Object.values(response.dates).flatMap((month: any) =>
      Array.isArray(month?.tickets) ? month.tickets : []
    );
  }

  private isVariantValid(variant: any, productFeatures: any): boolean {
    const validShape =
      variant !== null &&
      typeof variant === 'object' &&
      Number.isSafeInteger(Number(variant.id)) &&
      Number(variant.id) > 0 &&
      variant.features !== null &&
      typeof variant.features === 'object' &&
      variant.prices !== null &&
      typeof variant.prices === 'object';

    if (!validShape) {
      return false;
    }

    if (productFeatures.ticket || productFeatures.ballot) {
      return (
        (!variant.purchased || variant.anarchy) &&
        (variant.stock_quantity === null ||
          Number(variant.stock_quantity) > 0)
      );
    }

    return (
      variant.stockQuantity === null || Number(variant.stockQuantity) > 0
    );
  }

  private async getCurrentUser(): Promise<any | null> {
    const currentUser = this.userService.User.value?.User;

    if (this.hasValidId(currentUser)) {
      return currentUser;
    }

    try {
      const value = await firstValueFrom(
        this.userService.User.pipe(
          filter(result => this.hasValidId(result?.User)),
          take(1),
          timeout(this.userLoadTimeout)
        )
      );

      return value.User;
    } catch {
      return null;
    }
  }

  private hasValidId(value: any): boolean {
    const id = Number(value?.id);
    return Number.isSafeInteger(id) && id > 0;
  }

  private setLoadError(
    message: string,
    title = 'Unable to load checkout'
  ): void {
    this.loadError = message;
    this.loadErrorTitle = title;
    this.loading = false;
  }

  private redirectTo(path: string): void {
    window.location.replace(path);
  }
}
